import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import type { Order } from "@/types";
import { fetchOrdersBulk } from "@/lib/api";
import type { RootState } from "@/store";

/**
 * The customer's own order history.
 *
 * Recovered from the original's module 62877 (`recovery-source/SPEC_recent-orders.md`).
 * Its shape is deliberately minimal — the original carries exactly these two
 * fields, no error field and no timestamp — because every failure resolves to an
 * empty list rather than an error surface.
 */
interface HistoryState {
  recentOrders: Order[];
  recentOrdersLoading: boolean;
}

const initialState: HistoryState = {
  recentOrders: [],
  recentOrdersLoading: false,
};

/**
 * ONE bulk request for every id, not one request per order — the original posts
 * `{ order_ids }` to `/orders/:tenantId/bulk` and so do we.
 *
 * A rejection resolves to an empty list on purpose: the rail then renders
 * `null` and reports `hasRecentOrders: false` upward, which is the original's
 * behaviour. There is no toast and no retry.
 */
export const loadRecentOrders = createAsyncThunk<
  Order[],
  string[],
  { state: RootState }
>("history/fetchRecentOrders", async (orderIds, { getState, rejectWithValue }) => {
  const tenantId = getState().settings.data?._id;
  if (!tenantId) throw new Error("tenant_id not found");
  if (!orderIds.length) return [];
  try {
    return await fetchOrdersBulk(orderIds, tenantId);
  } catch {
    return rejectWithValue([]) as never;
  }
});

const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {
    resetHistory: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadRecentOrders.pending, (state) => {
        state.recentOrdersLoading = true;
      })
      .addCase(loadRecentOrders.fulfilled, (state, action) => {
        state.recentOrders = action.payload ?? [];
        state.recentOrdersLoading = false;
      })
      .addCase(loadRecentOrders.rejected, (state) => {
        state.recentOrders = [];
        state.recentOrdersLoading = false;
      });
  },
});

export const { resetHistory } = historySlice.actions;

const selectRecentOrders = (state: RootState) => state.history.recentOrders;
const selectOrderId = (_state: RootState, orderId: string | undefined) => orderId;

/** `history.recentOrders.find(o => o._id === id) ?? null`, memoised. */
export const selectRecentOrderById = createSelector(
  [selectRecentOrders, selectOrderId],
  (orders, orderId) => orders.find((order) => order._id === orderId) ?? null,
);

export default historySlice.reducer;
